/**
 * Samyang Pakistan - box builder and heat quiz.
 * Safe to load on any page.
 */

import { formatMoney } from '@theme/money-formatting';

/**
 * Builds the contents of a filled slot: the product photo when the flavour has
 * one, otherwise the drawn foil pack. Returns a DOM node — never a string — so
 * image URLs are set as properties and can never be parsed as markup.
 */
function slotContent(item) {
  if (item.img) {
    const img = document.createElement('img');
    img.src = item.img;
    img.alt = '';
    img.loading = 'lazy';
    return img;
  }

  const pack = document.createElement('span');
  pack.className = 'sy-pack sy-pack--sm';

  const crimpTop = document.createElement('span');
  crimpTop.className = 'sy-pack__crimp sy-pack__crimp--t';

  const kr = document.createElement('span');
  kr.className = 'sy-pack__kr';
  kr.style.color = item.hex;
  kr.style.top = '26%';
  kr.textContent = item.kr;

  const band = document.createElement('span');
  band.className = 'sy-pack__band';
  band.style.background = item.hex;

  const crimpBottom = document.createElement('span');
  crimpBottom.className = 'sy-pack__crimp sy-pack__crimp--b';

  pack.append(crimpTop, kr, band, crimpBottom);
  return pack;
}

/**
 * Fills slots with chosen flavours, then submits them to /cart/add as line
 * item properties on the box product. No app required.
 */
function initBoxBuilder(root) {
  if (root.dataset.syInit === 'true') return;
  root.dataset.syInit = 'true';

  const slotCount = parseInt(root.getAttribute('data-slots'), 10) || 5;
  const discount = parseInt(root.getAttribute('data-discount'), 10) || 0;
  // Currency formatting comes from Shopify, never from a setting someone can mistype.
  const moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
  const currencyCode = root.getAttribute('data-currency-code') || 'PKR';
  const discountCode = root.getAttribute('data-discount-code') || '';
  const cartAddUrl = root.getAttribute('data-cart-add-url') || '/cart/add';
  const cartUrl = root.getAttribute('data-cart-url') || '/cart';
  const slotsWrap = root.querySelector('[data-sy-slots]');
  const form = root.querySelector('[data-sy-box-form]');
  const itemsWrap = root.querySelector('[data-sy-box-items]');
  const countEl = root.querySelector('[data-sy-box-count]');
  const totalsEl = root.querySelector('[data-sy-box-totals]');
  const subtotalEl = root.querySelector('[data-sy-box-subtotal]');
  const finalEl = root.querySelector('[data-sy-box-final]');
  const submitBtn = root.querySelector('[data-sy-box-submit]');
  const resetBtn = root.querySelector('[data-sy-box-reset]');
  const errorEl = root.querySelector('[data-sy-box-error]');
  const picks = root.querySelectorAll('[data-sy-pick]');

  if (!slotsWrap || !form || !itemsWrap) return;

  /** Minor units to a display string, using the shop's own money format. */
  function money(minorUnits) {
    return formatMoney(minorUnits, moneyFormat, currencyCode);
  }

  /**
   * Where to send the customer once the packs are in the cart.
   *
   * Shopify's theme editor and theme previews identify themselves with query
   * parameters. Dropping them mid-flow sends the customer to a different theme
   * than the one they were looking at, so they are carried through to both the
   * discount URL and its redirect target.
   */
  function buildRedirect() {
    const PREVIEW_KEYS = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb'];
    const current = new URLSearchParams(window.location.search);

    const cart = new URL(cartUrl, window.location.origin);
    PREVIEW_KEYS.forEach((key) => {
      if (current.has(key)) cart.searchParams.set(key, current.get(key));
    });

    if (!discountCode) return cart.pathname + cart.search;

    const discountUrl = new URL('/discount/' + encodeURIComponent(discountCode), window.location.origin);
    discountUrl.searchParams.set('redirect', cart.pathname + cart.search);
    PREVIEW_KEYS.forEach((key) => {
      if (current.has(key)) discountUrl.searchParams.set(key, current.get(key));
    });

    return discountUrl.pathname + discountUrl.search;
  }

  let chosen = [];

  function render() {
    const slots = slotsWrap.querySelectorAll('.sy-box__slot');

    slots.forEach((slot, i) => {
      const item = chosen[i];
      if (item) {
        slot.classList.add('sy-box__slot--full');
        slot.replaceChildren(slotContent(item));
        slot.setAttribute('title', 'Remove ' + item.name);
      } else {
        slot.classList.remove('sy-box__slot--full');
        slot.textContent = '+';
        slot.removeAttribute('title');
      }
    });

    if (countEl) countEl.textContent = chosen.length + ' of ' + slotCount + ' chosen';

    const full = chosen.length === slotCount;
    if (submitBtn) submitBtn.disabled = !full;
    if (resetBtn) resetBtn.hidden = chosen.length === 0;

    // Sold-out flavours stay disabled; the rest lock once the box is full.
    picks.forEach((p) => {
      if (p.dataset.soldOut === 'true') return;
      p.disabled = full;
    });

    // Running total: real prices, minus the discount Shopify will apply.
    const subtotal = chosen.reduce((sum, item) => sum + item.price, 0);
    if (totalsEl) {
      if (chosen.length === 0) {
        totalsEl.hidden = true;
      } else {
        totalsEl.hidden = false;
        const off = full ? discount : 0;
        if (subtotalEl) {
          subtotalEl.textContent = off > 0 ? money(subtotal) : '';
          subtotalEl.hidden = off === 0;
        }
        if (finalEl) {
          finalEl.textContent =
            off > 0 ? money(subtotal - off) + ' — save ' + money(off) : money(subtotal);
        }
      }
    }

    // One cart line per flavour, so inventory decrements correctly.
    itemsWrap.replaceChildren();
    chosen.forEach((item, index) => {
      const id = document.createElement('input');
      id.type = 'hidden';
      id.name = 'items[' + index + '][id]';
      id.value = item.vid;

      const qty = document.createElement('input');
      qty.type = 'hidden';
      qty.name = 'items[' + index + '][quantity]';
      qty.value = '1';

      itemsWrap.append(id, qty);
    });
  }

  picks.forEach((pick) => {
    // Remember the server-rendered disabled state so render() never revives it.
    if (pick.disabled) pick.dataset.soldOut = 'true';

    pick.addEventListener('click', () => {
      if (chosen.length >= slotCount) return;
      chosen.push({
        name: pick.getAttribute('data-name'),
        vid: pick.getAttribute('data-vid'),
        price: parseInt(pick.getAttribute('data-price'), 10) || 0,
        hex: pick.getAttribute('data-hex'),
        kr: pick.getAttribute('data-kr'),
        img: pick.getAttribute('data-img') || '',
      });
      render();
    });
  });

  // Clicking a filled slot removes that pack.
  slotsWrap.addEventListener('click', (event) => {
    const slot = event.target.closest('.sy-box__slot');
    if (!slot) return;
    const index = parseInt(slot.getAttribute('data-slot-index'), 10) - 1;
    if (chosen[index]) {
      chosen.splice(index, 1);
      render();
    }
  });

  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      chosen = [];
      render();
    });
  }

  /**
   * Adds the chosen packs, then hands off to Shopify's discount URL so the
   * saving is applied only to carts built here. A customer who adds five packs
   * by hand never touches this path and so never gets the code.
   */
  form.addEventListener('submit', async (event) => {
    event.preventDefault();

    if (chosen.length !== slotCount || submitBtn?.disabled) return;

    if (errorEl) errorEl.hidden = true;
    if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new Error(problem?.description || problem?.message || 'Could not add the box.');
      }

      window.location.href = buildRedirect();
    } catch (error) {
      if (submitBtn) submitBtn.removeAttribute('aria-busy');
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.hidden = false;
      }
    }
  });

  render();
}

/**
 * Sums the answer scores and maps the total onto levels 1-6.
 */
function initQuiz(root) {
  if (root.dataset.syInit === 'true') return;
  root.dataset.syInit = 'true';

  const steps = root.querySelectorAll('[data-sy-quiz-step]');
  const stepsWrap = root.querySelector('[data-sy-quiz-steps]');
  const result = root.querySelector('[data-sy-quiz-result]');
  const levelEl = root.querySelector('[data-sy-quiz-level]');
  const verdictEl = root.querySelector('[data-sy-quiz-verdict]');
  const restart = root.querySelector('[data-sy-quiz-restart]');

  if (!steps.length || !result || !stepsWrap) return;

  const verdicts = {
    1: 'Jjajang. Sweet, savoury, barely any heat. A gentle way in.',
    2: 'Cheese. Creamy enough to take the edge off the chilli.',
    3: 'Carbonara. Mild by Buldak standards, still unmistakably Buldak.',
    4: 'Habanero Lime. Sharp and bright, and it does bite back.',
    5: 'Original Buldak. The one that started it. 4,404 SHU.',
    6: '2x Spicy. 8,706 SHU. Keep milk within reach.',
  };

  const maxPerQuestion = 3;
  let total = 0;
  let current = 0;

  function show(index) {
    steps.forEach((step, i) => {
      step.hidden = i !== index;
    });
  }

  function finish() {
    const ratio = total / (steps.length * maxPerQuestion);
    const level = Math.min(6, Math.max(1, Math.round(ratio * 6)));

    if (levelEl) levelEl.textContent = 'LV ' + level;
    if (verdictEl) verdictEl.textContent = verdicts[level];

    stepsWrap.hidden = true;
    result.hidden = false;
  }

  root.addEventListener('click', (event) => {
    const opt = event.target.closest('[data-sy-quiz-opt]');
    if (!opt) return;

    total += parseInt(opt.getAttribute('data-score'), 10) || 1;
    current += 1;

    if (current >= steps.length) {
      finish();
    } else {
      show(current);
    }
  });

  if (restart) {
    restart.addEventListener('click', () => {
      total = 0;
      current = 0;
      stepsWrap.hidden = false;
      result.hidden = true;
      show(0);
    });
  }
}

function boot() {
  document.querySelectorAll('[data-sy-box]').forEach(initBoxBuilder);
  document.querySelectorAll('[data-sy-quiz]').forEach(initQuiz);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Re-initialise when a section is re-rendered in the theme editor.
document.addEventListener('shopify:section:load', boot);
